import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Loader2, Save, X, Upload, Star, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { resolveImg } from "@/hooks/usePublicContent";
import { CATEGORIES } from "@/data/content";
import { SUBURBS } from "@/data/suburbs";

const EMPTY = { title: "", service: CATEGORIES[0].title, suburb: SUBURBS[0].name, summary: "", description: "", image: "", image_alt: "", gallery: [], status: "published", featured: false, order: 0 };
const input = "w-full bg-[#0f0f11] border border-white/15 focus:border-[#d4c4b7] text-stone-100 placeholder:text-stone-600 px-3 py-2.5 text-sm outline-none transition-colors";
const label = "block text-[0.65rem] uppercase tracking-[0.18em] text-stone-500 mb-2";

export default function AdminProjects() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // object or null
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [visible, setVisible] = useState(true);
  const [togglingVis, setTogglingVis] = useState(false);

  useEffect(() => { if (ready && !user) navigate("/admin/login"); }, [ready, user, navigate]);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get("/admin/projects"), api.get("/settings")]);
      setItems(p.data);
      setVisible(s.data?.projects_visible !== false);
    } catch { toast.error("Could not load projects."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const toggleVisibility = async () => {
    setTogglingVis(true);
    try {
      const { data } = await api.put("/admin/settings", { projects_visible: !visible });
      setVisible(data.projects_visible);
      toast.success(data.projects_visible ? "Recent Projects is now visible on your site." : "Recent Projects is now hidden from your site.");
    } catch { toast.error("Could not update visibility."); }
    finally { setTogglingVis(false); }
  };

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/projects/${editing.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success("Project saved.");
      setEditing(null); load();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try { await api.delete(`/admin/projects/${id}`); setItems((l) => l.filter((x) => x.id !== id)); toast.success("Deleted."); }
    catch { toast.error("Delete failed."); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/content/image", fd);
      setEditing((p) => ({ ...p, image: data.url }));
      toast.success("Image uploaded.");
    } catch { toast.error("Upload failed."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  if (!ready || !user) return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#d4c4b7]" /></div>;

  return (
    <div className="min-h-screen bg-[#0a0a0c]" data-testid="admin-projects">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto max-w-[1200px] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/admin" className="inline-flex items-center gap-2 text-stone-400 hover:text-white text-xs uppercase tracking-[0.2em]"><ArrowLeft className="h-3.5 w-3.5" /> Dashboard</a>
            <span className="hidden sm:block overline text-stone-500">Projects</span>
          </div>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 hover:text-[#d4c4b7] px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors"><LogOut className="h-3.5 w-3.5" /> Logout</button>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
          <h1 className="font-serif text-4xl text-stone-50 font-light">Recent Projects</h1>
          <button data-testid="project-new-btn" onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-[#9c7a4d] text-white hover:bg-[#b08d5a] px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors"><Plus className="h-4 w-4" /> New Project</button>
        </div>

        <div data-testid="projects-visibility-banner" className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 border p-5 mb-8 ${visible ? "border-emerald-400/30 bg-emerald-400/5" : "border-amber-400/30 bg-amber-400/5"}`}>
          <div className="flex items-start gap-3">
            {visible ? <Eye className="h-5 w-5 text-emerald-300 mt-0.5" /> : <EyeOff className="h-5 w-5 text-amber-300 mt-0.5" />}
            <div>
              <p className="text-stone-100 text-sm">Recent Projects section is <strong>{visible ? "visible on your website" : "hidden from your website"}</strong>.</p>
              <p className="text-stone-500 text-xs mt-1">{visible ? "It shows on your homepage and project pages are public and in your sitemap." : "It won't show on your homepage; project pages and sitemap entries are hidden. Your projects are still saved here — turn it back on anytime."}</p>
            </div>
          </div>
          <button
            data-testid="projects-visibility-toggle"
            onClick={toggleVisibility}
            disabled={togglingVis}
            className={`shrink-0 inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-60 ${visible ? "border border-white/15 text-stone-300 hover:border-amber-300 hover:text-amber-300" : "bg-emerald-500/80 text-white hover:bg-emerald-500"}`}
          >
            {togglingVis ? <Loader2 className="h-4 w-4 animate-spin" /> : visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {visible ? "Hide from site" : "Show on site"}
          </button>
        </div>

        {loading ? <Loader2 className="h-6 w-6 animate-spin text-[#d4c4b7]" /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((p) => (
              <div key={p.id} data-testid={`project-row-${p.slug}`} className="border border-white/10 bg-[#0f0f11] p-4 flex gap-4">
                {p.image ? <img src={resolveImg(p.image)} alt={p.image_alt || p.title} className="h-20 w-20 object-cover shrink-0" /> : <div className="h-20 w-20 bg-white/5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[0.6rem] uppercase tracking-[0.15em] px-2 py-0.5 border ${p.status === "published" ? "border-emerald-400/40 text-emerald-300" : "border-stone-500/40 text-stone-400"}`}>{p.status}</span>
                    {p.featured ? <span className="text-[0.6rem] uppercase tracking-[0.15em] text-[#d4c4b7]">Featured</span> : null}
                  </div>
                  <p className="text-stone-100 truncate">{p.title}</p>
                  <p className="text-stone-500 text-xs mt-0.5 truncate">{p.service} · {p.suburb}</p>
                  <div className="flex gap-3 mt-3">
                    <button data-testid={`project-edit-${p.slug}`} onClick={() => setEditing({ ...EMPTY, ...p })} className="text-xs uppercase tracking-[0.18em] text-stone-300 hover:text-[#d4c4b7]">Edit</button>
                    <button onClick={() => remove(p.id)} className="text-xs uppercase tracking-[0.18em] text-red-300/70 hover:text-red-300 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
                  </div>
                </div>
              </div>
            ))}
            {items.length === 0 ? <p className="text-stone-500">No projects yet. Add your first one.</p> : null}
          </div>
        )}
      </div>

      {editing ? (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-lenis-prevent onClick={() => setEditing(null)}>
          <div className="bg-[#0f0f11] border border-white/10 w-full max-w-2xl my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="project-editor">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-stone-50">{editing.id ? "Edit project" : "New project"}</h2>
              <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className={label}>Title</label><input data-testid="project-title" className={input} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
              <div><label className={label}>Service</label><select className={input} value={editing.service} onChange={(e) => setEditing({ ...editing, service: e.target.value })}>{CATEGORIES.map((c) => <option key={c.id} value={c.title}>{c.title}</option>)}</select></div>
              <div><label className={label}>Suburb</label><select className={input} value={editing.suburb} onChange={(e) => setEditing({ ...editing, suburb: e.target.value })}>{SUBURBS.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}</select></div>
              <div className="sm:col-span-2"><label className={label}>Summary (shown on cards)</label><input data-testid="project-summary" className={input} value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className={label}>Description</label><textarea rows={4} className={`${input} resize-none`} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="sm:col-span-2">
                <label className={label}>Image</label>
                <div className="flex gap-3 items-center">
                  <input className={input} placeholder="Image URL" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} />
                  <label className="shrink-0 inline-flex items-center gap-2 border border-white/15 hover:border-[#d4c4b7] text-stone-300 px-3 py-2.5 text-xs uppercase tracking-[0.16em] cursor-pointer">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
                    <input type="file" accept="image/*" className="hidden" onChange={uploadImage} />
                  </label>
                </div>
                {editing.image ? <img src={resolveImg(editing.image)} alt="preview" className="mt-3 h-28 w-full object-cover border border-white/10" /> : null}
              </div>
              <div className="sm:col-span-2"><label className={label}>Image alt text (SEO/accessibility)</label><input data-testid="project-alt" className={input} value={editing.image_alt} onChange={(e) => setEditing({ ...editing, image_alt: e.target.value })} /></div>
              <div><label className={label}>Status</label><select data-testid="project-status" className={input} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}><option value="published">Published</option><option value="draft">Draft</option></select></div>
              <div><label className={label}>Order</label><input type="number" className={input} value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
              <label className="sm:col-span-2 flex items-center gap-3 text-stone-300 text-sm cursor-pointer"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Feature this project</label>
            </div>
            <div className="flex gap-3 mt-6">
              <button data-testid="project-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#9c7a4d] text-white hover:bg-[#b08d5a] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.2em]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
              <button onClick={() => setEditing(null)} className="border border-white/15 text-stone-300 hover:text-white px-6 py-3 text-xs uppercase tracking-[0.2em]">Cancel</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
